import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '../../contexts/ThemeContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from './Button'

const { FiSun, FiMoon, FiMonitor, FiChevronDown } = FiIcons

const ThemeToggle = ({ variant = 'button', size = 'md', showLabel = false, className = '' }) => {
  const { theme, setLightTheme, setDarkTheme, setSystemTheme, mounted } = useTheme()
  const [showDropdown, setShowDropdown] = useState(false)

  if (!mounted) {
    return <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
  }

  const themeOptions = [
    {
      key: 'light',
      label: 'Light',
      icon: FiSun,
      action: setLightTheme,
      description: 'Light theme'
    },
    {
      key: 'dark',
      label: 'Dark',
      icon: FiMoon,
      action: setDarkTheme,
      description: 'Dark theme'
    },
    {
      key: 'system',
      label: 'System',
      icon: FiMonitor,
      action: setSystemTheme,
      description: 'Follow system preference'
    }
  ]

  const currentTheme = themeOptions.find(option => option.key === theme) || themeOptions[0]

  if (variant === 'simple') {
    return (
      <Button
        variant="ghost"
        size={size}
        onClick={() => theme === 'light' ? setDarkTheme() : setLightTheme()}
        className={`p-2 ${className}`}
        title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      >
        <SafeIcon 
          icon={theme === 'light' ? FiMoon : FiSun} 
          className={`${size === 'sm' ? 'text-lg' : size === 'lg' ? 'text-2xl' : 'text-xl'}`} 
        />
        {showLabel && (
          <span className="ml-2 capitalize">{theme}</span>
        )}
      </Button>
    )
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size={size}
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-2 p-2 ${className}`}
      >
        <SafeIcon 
          icon={currentTheme.icon} 
          className={`${size === 'sm' ? 'text-lg' : size === 'lg' ? 'text-2xl' : 'text-xl'}`} 
        />
        {showLabel && (
          <>
            <span className="capitalize">{currentTheme.label}</span>
            <SafeIcon icon={FiChevronDown} className="text-sm" />
          </>
        )}
      </Button>

      <AnimatePresence>
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Dropdown Menu */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute right-0 top-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 min-w-[160px]"
            >
              {themeOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => {
                    option.action()
                    setShowDropdown(false)
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors
                    ${theme === option.key 
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }
                  `}
                >
                  <SafeIcon 
                    icon={option.icon} 
                    className={`text-lg ${theme === option.key ? 'text-primary-500' : 'text-gray-500 dark:text-gray-400'}`} 
                  />
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {option.description}
                    </div>
                  </div>
                  {theme === option.key && (
                    <div className="w-2 h-2 bg-primary-500 rounded-full ml-auto" />
                  )}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ThemeToggle